// Hook: Mindful order manager — wraps useOrderManager with guard evaluation
import { useState, useCallback } from 'react';
import { useOrderManager } from './useOrderManager';
import { useMindGuards } from './useMindGuards';
import { useMind } from '../context/MindContext';

export function useMindfulOrderManager() {
  const orderManager = useOrderManager();
  const { evaluatePreTradeGuards } = useMindGuards();
  const { showGuardModal, updatePerformanceAfterTrade, logBehavior } = useMind();
  const [guardLoading, setGuardLoading] = useState(false);

  const submitOrder = useCallback(async (orderData) => {
    setGuardLoading(true);

    // Evaluate guards
    const evaluation = evaluatePreTradeGuards(orderData);

    if (evaluation.allowed) {
      // No guards triggered — proceed directly
      setGuardLoading(false);
      const result = await orderManager.submitOrder(orderData);
      logBehavior({ type: 'ORDER_PLACED', order: orderData, guardsPassed: true });
      return result;
    }

    if (evaluation.blocked) {
      // Hard block — cannot override
      setGuardLoading(false);
      logBehavior({
        type: 'ORDER_BLOCKED',
        order: orderData,
        guards: evaluation.guards.map(g => g.id),
      });
      return {
        status: false,
        message: evaluation.guards.find(g => !g.overridable)?.description || 'Order blocked by risk guard.',
        blocked: true,
        guards: evaluation.guards,
      };
    }

    // Warn — show modal, let user override or cancel
    setGuardLoading(false);
    return new Promise((resolve) => {
      showGuardModal(
        evaluation.guards,
        orderData,
        // onOverride
        async () => {
          const result = await orderManager.submitOrder(orderData);
          logBehavior({
            type: 'ORDER_PLACED_WITH_OVERRIDE',
            order: orderData,
            guards: evaluation.guards.map(g => g.id),
          });
          resolve(result);
        },
        // onCancel
        () => {
          resolve({ status: false, message: 'Order cancelled by trader.', cancelled: true });
        }
      );
    });
  }, [evaluatePreTradeGuards, orderManager, showGuardModal, logBehavior]);

  return {
    ...orderManager,
    submitOrder,
    guardLoading,
    updatePerformanceAfterTrade,
  };
}
